/**
 * Task Templates Redux Slice
 * Feature: Generic Configurable Task System
 * Branch: 022-i-like-all
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  TaskTemplate,
  TaskTemplatesState,
  CreateTaskTemplateRequest,
  UpdateTaskTemplateRequest,
  TaskTemplateFilters,
  UseTaskTemplateRequest,
  TaskInstance,
  TaskType,
} from "../types/taskTemplate.types";
import {
  createTaskTemplate,
  updateTaskTemplate,
  deleteTaskTemplate,
  fetchTaskTemplates,
  useTaskTemplate,
} from "@/api/taskTemplates";

// Initial state
const initialState: TaskTemplatesState = {
  templates: [],
  selectedTemplate: null,
  isLoading: false,
  error: null,
  filters: {},
  totalCount: 0,
};

// Async thunks for API operations
export const fetchTemplates = createAsyncThunk(
  "taskTemplates/fetchTemplates",
  async (filters: TaskTemplateFilters = {}) => {
    const response = await fetchTaskTemplates(filters);
    return response;
  },
);

export const createTemplate = createAsyncThunk(
  "taskTemplates/createTemplate",
  async (data: CreateTaskTemplateRequest) => {
    const response = await createTaskTemplate(data);
    return response.data;
  },
);

export const updateTemplateAsync = createAsyncThunk(
  "taskTemplates/updateTemplate",
  async ({ id, data }: { id: string; data: UpdateTaskTemplateRequest }) => {
    const response = await updateTaskTemplate(id, data);
    return response.data;
  },
);

export const deleteTemplateAsync = createAsyncThunk(
  "taskTemplates/deleteTemplate",
  async (id: string) => {
    await deleteTaskTemplate(id);
    return id;
  },
);

export const useTemplateAsync = createAsyncThunk(
  "taskTemplates/useTemplate",
  async ({ id, data }: { id: string; data: UseTaskTemplateRequest }) => {
    const response = await useTaskTemplate(id, data);
    return response.data;
  },
);

// Redux slice
const taskTemplatesSlice = createSlice({
  name: "taskTemplates",
  initialState,
  reducers: {
    // Synchronous actions
    selectTemplate: (state, action: PayloadAction<TaskTemplate | null>) => {
      state.selectedTemplate = action.payload;
    },

    setFilters: (state, action: PayloadAction<TaskTemplateFilters>) => {
      state.filters = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Optimistic update for template modification
    optimisticUpdateTemplate: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<TaskTemplate> }>,
    ) => {
      const index = state.templates.findIndex(
        (t) => t.id === action.payload.id,
      );
      if (index !== -1) {
        state.templates[index] = {
          ...state.templates[index],
          ...action.payload.changes,
          updatedAt: new Date(),
        };
      }
      if (state.selectedTemplate?.id === action.payload.id) {
        state.selectedTemplate = {
          ...state.selectedTemplate,
          ...action.payload.changes,
          updatedAt: new Date(),
        };
      }
    },

    // Optimistic delete (soft delete)
    optimisticDeleteTemplate: (state, action: PayloadAction<string>) => {
      const index = state.templates.findIndex((t) => t.id === action.payload);
      if (index !== -1) {
        state.templates[index].deletedAt = new Date();
        state.templates[index].isActive = false;
      }
      if (state.selectedTemplate?.id === action.payload) {
        state.selectedTemplate = null;
      }
    },

    // Real-time update from GraphQL subscription
    templateUpdatedFromSubscription: (
      state,
      action: PayloadAction<TaskTemplate>,
    ) => {
      const index = state.templates.findIndex(
        (t) => t.id === action.payload.id,
      );
      if (index !== -1) {
        state.templates[index] = action.payload;
      } else {
        // New template, add to list if it matches filters
        if (shouldIncludeTemplate(action.payload, state.filters)) {
          state.templates.push(action.payload);
          state.totalCount += 1;
        }
      }

      if (state.selectedTemplate?.id === action.payload.id) {
        state.selectedTemplate = action.payload;
      }
    },

    // Handle template propagation updates
    instancesUpdatedFromPropagation: (
      state,
      action: PayloadAction<{ templateId: string; instanceCount: number }>,
    ) => {
      // Update any UI indicators about instance updates
      const template = state.templates.find(
        (t) => t.id === action.payload.templateId,
      );
      if (template) {
        // Could add a field like `lastPropagatedAt` or `instanceUpdateCount`
        (template as any).instanceUpdateCount = action.payload.instanceCount;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch templates
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        // The API always returns TaskTemplatesListResponse format
        state.templates = action.payload.templates;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch templates";
      });

    // Create template
    builder
      .addCase(createTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates.unshift(action.payload);
        state.totalCount += 1;
        state.selectedTemplate = action.payload;
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to create template";
      });

    // Update template
    builder
      .addCase(updateTemplateAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTemplateAsync.fulfilled, (state, action) => {
        const index = state.templates.findIndex(
          (t) => t.id === action.payload.id,
        );
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
        if (state.selectedTemplate?.id === action.payload.id) {
          state.selectedTemplate = action.payload;
        }
      })
      .addCase(updateTemplateAsync.rejected, (state, action) => {
        state.error = action.error.message || "Failed to update template";
        // Rollback optimistic update could be implemented here
      });

    // Delete template
    builder
      .addCase(deleteTemplateAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteTemplateAsync.fulfilled, (state, action) => {
        const index = state.templates.findIndex((t) => t.id === action.payload);
        if (index !== -1) {
          // Soft delete - mark as deleted
          state.templates[index].deletedAt = new Date();
          state.templates[index].isActive = false;
        }
        if (state.selectedTemplate?.id === action.payload) {
          state.selectedTemplate = null;
        }
      })
      .addCase(deleteTemplateAsync.rejected, (state, action) => {
        state.error = action.error.message || "Failed to delete template";
        // Rollback optimistic delete could be implemented here
      });

    // Use template
    builder
      .addCase(useTemplateAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(useTemplateAsync.fulfilled, (state, action) => {
        // Could update usage statistics or other metadata
        const template = state.templates.find(
          (t) => t.id === action.payload.templateId,
        );
        if (template) {
          (template as any).lastUsedAt = new Date();
          (template as any).usageCount =
            ((template as any).usageCount || 0) + 1;
        }
      })
      .addCase(useTemplateAsync.rejected, (state, action) => {
        state.error = action.error.message || "Failed to use template";
      });
  },
});

// Helper function to check if template matches filters
function shouldIncludeTemplate(
  template: TaskTemplate,
  filters: TaskTemplateFilters,
): boolean {
  if (filters.type && template.type !== filters.type) {
    return false;
  }
  if (
    filters.isActive !== undefined &&
    template.isActive !== filters.isActive
  ) {
    return false;
  }
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    const matchesName = template.name.toLowerCase().includes(searchLower);
    const matchesDescription = template.description
      ?.toLowerCase()
      .includes(searchLower);
    if (!matchesName && !matchesDescription) {
      return false;
    }
  }
  if (filters.createdBy && template.createdBy !== filters.createdBy) {
    return false;
  }
  return true;
}

// Export actions
export const {
  selectTemplate,
  setFilters,
  clearError,
  optimisticUpdateTemplate,
  optimisticDeleteTemplate,
  templateUpdatedFromSubscription,
  instancesUpdatedFromPropagation,
} = taskTemplatesSlice.actions;

// Export reducer
export default taskTemplatesSlice.reducer;

// Selectors
export const selectAllTemplates = (state: {
  taskTemplates: TaskTemplatesState;
}) => state.taskTemplates.templates;

export const selectActiveTemplates = (state: {
  taskTemplates: TaskTemplatesState;
}) => state.taskTemplates.templates.filter((t) => t.isActive && !t.deletedAt);

export const selectTemplatesByType = (
  state: { taskTemplates: TaskTemplatesState },
  type: TaskType,
) =>
  state.taskTemplates.templates.filter(
    (t) => t.type === type && t.isActive && !t.deletedAt,
  );

export const selectSelectedTemplate = (state: {
  taskTemplates: TaskTemplatesState;
}) => state.taskTemplates.selectedTemplate;

export const selectTemplatesLoading = (state: {
  taskTemplates: TaskTemplatesState;
}) => state.taskTemplates.isLoading;

export const selectTemplatesError = (state: {
  taskTemplates: TaskTemplatesState;
}) => state.taskTemplates.error;
